/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, Fragment } from "react";
import PageNumberUICss from "./PageNumberUI.css";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getViewMode } from "selectors/viewModeSelectors";
import { hudDisabled } from "selectors/hudSelectors";
import { docViewModes } from "constants/enums";
import { TOOLTIP_TOP_PLACEMENT } from "constants/constants";
import * as stores from "stores";
import classNames from "classnames";
import withToolTip from "dc-preview-utils/src/HOC/withToolTip";

const withShortcutLabel = (txt, shortcutLabel) => (shortcutLabel ? `${txt} (${shortcutLabel})` : txt);

class PageNumberUI extends Component {
  getInputElement(style) {
    return (
      <PageNumberInputWithToolTip
        style={style}
        tooltip={{
          text: this.props.intl.formatMessage({ id: this.props.inputTooltipString }),
          shortcut: this.props.shortcutLabel,
          placement: this.props.inputTooltipPlacement
        }}
        {...this.props}
      />
    );
  }

  getTotalPageNumberElement(style) {
    const { useModernViewer, totalPagesTooltip, totalPagesTooltipPlacement, fileExtension, intl } = this.props;

    let totalPages = "--";
    if (this.props.totalPages) {
      totalPages = this.props.totalPages;
    }

    if (useModernViewer) {
      return (
        <TotalPageNumberElementWithTooltip
          style={style}
          totalPages={totalPages}
          tooltip={{
            text: intl.formatMessage({ id: totalPagesTooltip }, { fileExtension }),
            placement: totalPagesTooltipPlacement
          }}
        />
      );
    }

    return (
      <div className={PageNumberUICss.totalPages} style={style}>
        <span className={PageNumberUICss.PageNumberSelectorLeftSpan} />/
        <span className={PageNumberUICss.PageNumberSelectorRightSpan}> {totalPages} </span>
      </div>
    );
  }

  render() {
    let style = {};
    if (isCurrentDeviceMobile() || isCurrentDeviceTablet()) {
      style.fontSize = "16px";
    }

    if (getViewMode(stores) !== docViewModes.IN_PLACE && getViewMode(stores) !== docViewModes.SINGLE_PAGE) {
      style.marginRight = "8px";
    }

    const { useModernViewer } = this.props;

    let wrapperClassName = "";
    if (useModernViewer && hudDisabled(stores)) {
      wrapperClassName = classNames(wrapperClassName, PageNumberUICss.PageNumberUIContainerModern);
      style.marginTop = "8px";
    } else {
      wrapperClassName = classNames(wrapperClassName, PageNumberUICss.PageNumberUIContainer);
    }

    const pageNumberInputElement = this.getInputElement(style);
    const totalPageNumberElement = this.getTotalPageNumberElement(style);

    return (
      <div className={wrapperClassName}>
        {pageNumberInputElement}
        <Fragment>{totalPageNumberElement}</Fragment>
      </div>
    );
  }
}
class PageNumberInput extends Component {
  constructor(props) {
    super(props);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
  }

  updatePageNumber() {
    if (this.props.currentPage) {
      this.pageNumber.value = this.props.currentPage;
    } else {
      this.pageNumber.value = "--";
    }
  }

  componentDidMount() {
    this.updatePageNumber = this.updatePageNumber.bind(this);
    this.updatePageNumber();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.currentPage !== this.props.currentPage) {
      this.updatePageNumber();
    }
  }

  onKeyPress(event) {
    if (this.props.handleKeyPress(event, this.pageNumber.value)) {
      this.pageNumber.blur();
    }
  }

  onKeyDown(event) {
    this.props.handleKeyDown(event);
  }

  onInputBlur() {
    this.updatePageNumber();
    if (this.props.onBlur) this.props.onBlur();
  }

  render() {
    const { useModernViewer, inputAriaLabel } = this.props;

    let className = "";
    if (useModernViewer) {
      className = classNames(className, PageNumberUICss.pageNumberInputModern);
      if (getViewMode(stores) === docViewModes.IN_PLACE || getViewMode(stores) === docViewModes.SINGLE_PAGE) {
        className = classNames(className, PageNumberUICss.pageNumberInputModernHUD);
      }
    } else className = classNames(className, PageNumberUICss.pageNumberInput);

    return (
      <input
        type="text"
        id="PageNumberUIModern"
        data-test-id="PageNumberInput"
        aria-label={withShortcutLabel(this.props.intl.formatMessage({ id: inputAriaLabel }), this.props.shortcutLabel)}
        className={className}
        style={this.props.style}
        ref={input => {
          this.pageNumber = input;
          this.props.setPageNumberInputHandle(input);
        }}
        onFocus={this.props.onFocus}
        onBlur={this.onInputBlur}
        disabled={!this.props.shouldStartRendering}
        onKeyPress={this.onKeyPress}
        onKeyDown={this.onKeyDown}
      />
    );
  }
}

const PageNumberInputWithToolTip = withToolTip(PageNumberInput);

const TotalPageNumberElement = ({ style, totalPages }) => (
  <div className={PageNumberUICss.totalPagesModern} style={style} data-test-id="total-page-number">
    {totalPages}
  </div>
);
const TotalPageNumberElementWithTooltip = withToolTip(TotalPageNumberElement);

PageNumberUI.defaultProps = {
  inputTooltipPlacement: TOOLTIP_TOP_PLACEMENT,
  totalPagesTooltipPlacement: TOOLTIP_TOP_PLACEMENT
};

export { PageNumberUI };
